// Ocupa el lugar del catálogo cuando el servicio de reportes no se puede leer, con un mensaje
// distinto para no disponible, sin permiso y falla.
import { AlertTriangle, Lock } from 'lucide-react';
import { Button } from '@/kit';
import { cn } from '@/lib/cn';
import { CREDIT_REPORTS_FORBIDDEN_DESCRIPTION } from '../permissions';

export type ReportsServiceStateKind = 'unavailable' | 'forbidden' | 'failed';

const COPY: Record<ReportsServiceStateKind, { title: string; description: string }> = {
    unavailable: {
        title: 'El servicio de reportes todavía no está disponible',
        description:
            'Este ambiente todavía no tiene el servicio de reportes, así que no hay catálogo que leer ni corridas que mostrar. En cuanto esté disponible, el catálogo y el historial aparecen solos.',
    },
    forbidden: {
        title: 'No tienes permiso para ver los reportes',
        description: CREDIT_REPORTS_FORBIDDEN_DESCRIPTION,
    },
    failed: {
        title: 'El servicio de reportes falló',
        description:
            'No pudimos cargar el catálogo de reportes. Intenta de nuevo; si sigue pasando, repórtalo.',
    },
};

export function ReportsServiceState({
    kind,
    detail,
    onRetry,
}: {
    kind: ReportsServiceStateKind;
    /** Mensaje del error ya apto para el operador; solo se muestra en `failed`. */
    detail?: string;
    onRetry: () => void;
}) {
    const { title, description } = COPY[kind];
    const Icon = kind === 'forbidden' ? Lock : AlertTriangle;

    return (
        <div
            role="alert"
            className={cn(
                'flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-6 py-12 text-center',
                kind === 'failed'
                    ? 'border-destructive/40 bg-destructive/5'
                    : 'border-warning-soft-border bg-warning-soft/50',
            )}
        >
            <Icon
                className={cn('size-6', kind === 'failed' ? 'text-destructive' : 'text-warning')}
                aria-hidden
            />
            <h2 className="text-[14px] font-semibold">{title}</h2>
            <p className="max-w-xl text-[11.5px] leading-relaxed text-muted-foreground">
                {description}
            </p>
            {kind === 'failed' ? (
                <>
                    {detail ? (
                        <p className="max-w-xl text-[10.5px] leading-relaxed text-muted-foreground">
                            {detail}
                        </p>
                    ) : null}
                    <Button variant="secondary" size="sm" onClick={onRetry}>
                        Reintentar
                    </Button>
                </>
            ) : null}
        </div>
    );
}
